// ============================================================================
// ReportHeader — professional identity + parameter band displayed at the top
// of every report screen (Day Book, Ledger Statement, Open Bills, and future
// reports). Design pattern: horizontal info-tile row with subtle iconography,
// vertical dividers, and a left accent bar. Modelled on the report-context
// bands used by NetSuite, QuickBooks Enterprise, and Sage Intacct.
//
// Renders three groups only:
//   - Entity name        (from useAuth().selectedEntity)   — home icon
//   - Financial Year     (from useAuth().selectedFinancialYear) — calendar icon
//   - Applied filters    (caller-supplied — the report-specific parameters)
//                        Icons auto-inferred from label; caller may override.
//
// The "Generated at" and "Generated by" attributions are shown ONLY in the
// exported Excel workbook (see Fbp.Api ReportExcelBuilder) — audit metadata
// that belongs on the printout, not on the on-screen chrome.
//
// The Export button lives on the ModuleHeader's actionButtons array via
// actionButtonFactory.export_() — this component intentionally has no
// export UI of its own.
//
// Wave D. Domain-neutral — port to enterprise-templates/react-project-template
// once stable so every scaffolded app inherits the same header shape.
// ============================================================================

import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { useAuth } from 'hooks/usePermissions';

type IconKey = keyof typeof IconSystem;

export interface ReportFilter {
  label: string;
  /** Human-readable value. Fall back to '—' when empty. */
  value: string | number | null | undefined;
  /** IconSystem key. When omitted, ReportHeader picks a sensible default from the label. */
  icon?: IconKey;
}

export interface ReportHeaderProps {
  /** Report-specific applied filters, e.g. [{ label: 'Period', value: '01/07/2026 — 07/07/2026' }] */
  filters?: ReportFilter[];
}

// ----------------------------------------------------------------------------
// Icon inference — maps common metadata labels to IconSystem entries so callers
// don't have to specify icons for the well-known cases. Extend as new report
// filters appear (e.g. 'Party' → user, 'Currency' → dollar, ...).
// ----------------------------------------------------------------------------
const inferIcon = (label: string): IconKey => {
  const key = label.toLowerCase();
  if (key.includes('period') || key.includes('date') || key.includes('year')) return 'calendar';
  if (key.includes('ledger') || key.includes('account')) return 'folder';
  if (key.includes('party') || key.includes('customer') || key.includes('supplier')) return 'user';
  if (key.includes('closed') || key.includes('include') || key.includes('status')) return 'filter';
  return 'tag';
};

// ----------------------------------------------------------------------------
// Tile — one label + value cell with a small icon on the left.
// ----------------------------------------------------------------------------
const Tile: React.FC<{ label: string; value: React.ReactNode; iconKey: IconKey; primary?: boolean }> = ({
  label,
  value,
  iconKey,
  primary,
}) => {
  const Icon = IconSystem[iconKey] as React.ComponentType<{ size?: number; color?: string }>;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.4rem',
        padding: '0 0.65rem',
        minWidth: 0,
        flex: '0 0 auto',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: primary ? 'var(--primary-color, #2563eb)' : 'var(--text-muted)',
          flexShrink: 0,
        }}
        aria-hidden
      >
        <Icon size={12} />
      </span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0, minWidth: 0 }}>
        <span
          style={{
            fontSize: '0.6rem',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
            fontWeight: 600,
            lineHeight: 1,
          }}
        >
          {label}
        </span>
        <span
          style={{
            fontSize: '0.75rem',
            fontWeight: primary ? 700 : 600,
            color: 'var(--text-primary)',
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '18rem',
          }}
          title={typeof value === 'string' ? value : undefined}
        >
          {value}
        </span>
      </div>
    </div>
  );
};

const Divider = () => (
  <div
    aria-hidden
    style={{
      width: 1,
      alignSelf: 'stretch',
      background: 'var(--border-color)',
      margin: '0.25rem 0',
    }}
  />
);

const ReportHeader: React.FC<ReportHeaderProps> = ({ filters }) => {
  const { selectedEntity, selectedFinancialYear } = useAuth();
  const fyLabel = selectedFinancialYear?.code || selectedFinancialYear?.name || '—';

  const tiles: Array<{ label: string; value: React.ReactNode; iconKey: IconKey; primary?: boolean }> = [
    { label: 'Entity', value: selectedEntity?.name || '—', iconKey: 'home', primary: true },
    { label: 'Financial Year', value: fyLabel, iconKey: 'calendar', primary: true },
    ...(filters ?? []).map(f => ({
      label: f.label,
      value: f.value != null && f.value !== '' ? String(f.value) : '—',
      iconKey: f.icon ?? inferIcon(f.label),
    })),
  ];

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'stretch',
        flexWrap: 'wrap',
        rowGap: '0.35rem',
        padding: '0.4rem 0.3rem',
        paddingLeft: '0.65rem',
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border-color)',
        borderRadius: '0.5rem 0.5rem 0 0',
        borderBottom: 'none',
        overflow: 'hidden',
      }}
    >
      {/* Left accent bar — 3px vertical strip signalling "report context band" */}
      <span
        aria-hidden
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          width: 3,
          background: 'var(--primary-color, #2563eb)',
          borderRadius: '0.5rem 0 0 0',
        }}
      />
      {tiles.map((t, i) => (
        <React.Fragment key={`${t.label}-${i}`}>
          {i > 0 && <Divider />}
          <Tile label={t.label} value={t.value} iconKey={t.iconKey} primary={t.primary} />
        </React.Fragment>
      ))}
    </div>
  );
};

export default ReportHeader;
